import { createContext, useContext, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getWork, addWork, updateWork, deleteWorkItem } from "../../api/work";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { useNavigate } from "react-router-dom";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const workArray = useRef();
  const toaster = useToaster();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [term, setTerm] = useState("");


  const getWorkQuery = useQuery({
    queryKey: ["works", term], 
    queryFn: () => getWork(term), 
  });
  

  const addWorkQuery = useMutation({ mutationFn: addWork });
  const updateWorkQuery = useMutation({ mutationFn: updateWork });
  const deleteWorkItemQuery = useMutation({ mutationFn: deleteWorkItem });

  const initialValues = useMemo(() => {
    const workData = getWorkQuery?.data?.data?.[0] || {};
    return {
      id: workData?._id,
      meta: {
        meta_title: workData?.meta_title || "",
        meta_slug_url: workData?.meta_slug_url || "",
        meta_description: workData?.meta_description || "",
        keywords: workData?.keywords || "",
      },
      banner: {
        heading: workData?.heading || "",
        sub_heading: workData?.sub_heading || "",
        description: workData?.description || "",
        cta_label: workData?.cta_label || "",
        cta_redirect_link: workData?.cta_redirect_link || "",
        works: workData?.works?.map((item) => ({ ...item, id: item._id, order_index: item.order_index !== undefined ? item.order_index : "" })) || [{ title: "", description: "", order_index: "" }],
      },
      catalog_download: {
        title: workData?.catalog_download?.title || "",
        file: workData?.catalog_download?.file || "",
      },

      brochure_download: {
        title: workData?.brochure_download?.title || "",
        file: workData?.brochure_download?.file || "",
      },
    };
  }, [getWorkQuery]);

  const handleSubmit = async (values) => {
    const payload = {
      meta_title: values?.meta?.meta_title || "",
      meta_slug_url: values?.meta?.meta_slug_url || "",
      meta_description: values?.meta?.meta_description || "",
      keywords: values?.meta?.keywords || "",

      heading: values?.banner?.heading || "",
      sub_heading: values?.banner?.sub_heading || "",
      description: values?.banner?.description || "",
      cta_label: values?.banner?.cta_label || "",
      cta_redirect_link: values?.banner?.cta_redirect_link || "",

      brochure_download: {
        title: values?.brochure_download?.title || "",
        file: values?.brochure_download?.file || "",
      },

      catalog_download: {
        title: values?.catalog_download?.title || "",
        file: values?.catalog_download?.file || "",
      },
    };
    const workData = getWorkQuery?.data?.data?.[0] || {};

    if (workData._id) {
      payload.id = workData._id;
      updateWorkQuery.mutate(payload, {
        onSuccess: () => {
          toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
        },
      });
      return;
    }
    addWorkQuery.mutate(payload, {
      onSuccess: () => {
        toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
      },
    });
  };

  const handleWorkAdd = () => {
    workArray.current.push({ heading: "", description: "", image: "" });
  };

  const handleWorkDelete = async (item, values) => {
    deleteWorkItemQuery.mutate(
      { id: values.id, itemId: item.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["works"]);
          toaster.success(Const.MESSAGES.SUCCESS, "Work is deleted successfully.");
        },
        onError: () => {
          toaster.error(Const.MESSAGES.FAILED, "Internal Server Error");
        },
      }
    );
  };

  const handleEditClick = (item, values) => {
    navigate(`/addwork/${values.id}?itemId=${item.id}`);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    setTerm(value);
  };
  

  return (
    <PageContext.Provider
      value={{
        initialValues,
        handleWorkDelete,
        handleWorkAdd,
        workArray,
        handleSubmit,
        getWorkQuery,
        handleEditClick,
        handleOnChange,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const useWorkProvider = () => {
  return useContext(PageContext);
};
